import { BottzappingInsight, BotzappingDatedSummary } from 'Features/home/api/types'
import {
  ALLOWED_THREAT_GROUPS_NAMES,
  THREAT_GROUPS_TO_DISPLAY,
  TREND_CATEGORIES,
} from '../../routes/Home/constants'
import i18n from 'Lib/i18n'

export interface NumericDashboardSummaryData {
  protectedVisits?: number
  protectedValidVisits?: number
  whiteListed?: number
  visits: number
  invalid: number
  blockedCookies?: number
  paidBlockedCookies?: number
  directTraffic?: number
  paidTraffic?: number
  organicTraffic?: number
}

export interface DashboardSummaryDataEntry extends NumericDashboardSummaryData {
  domain?: string
  unixTimestamp?: number
  threatGroup?: string
  threatType?: string
  campaign?: string
  keyword?: string
  country?: string
}

export type ThreatGroupsBreakdown<T> = Record<ALLOWED_THREAT_GROUPS_NAMES, T>

export type ReducedTrendsAndThreatGroups = {
  [TREND_CATEGORIES.INVALID_RATE]: Record<
    number,
    { paidTraffic: number; invalid: number; rate: number }
  >
  [TREND_CATEGORIES.VISITS]: Record<number, [number, number]>
  threatGroupsBreakdown: ThreatGroupsBreakdown<ThreatGroupData>
}

export const reducedTrendsAndThreatGroupsBuilder: {
  byDate: () => ReducedTrendsAndThreatGroups
  byDomain: () => Omit<ReducedTrendsAndThreatGroups, typeof TREND_CATEGORIES.INVALID_RATE>
  buildThreatGroupsBreakdown: () => Pick<ReducedTrendsAndThreatGroups, 'threatGroupsBreakdown'>
} = {
  buildThreatGroupsBreakdown: () => ({
    threatGroupsBreakdown: {
      [THREAT_GROUPS_TO_DISPLAY.INVALID_BOT_ACTIVITY.value]: new ThreatGroupData(
        i18n.t(THREAT_GROUPS_TO_DISPLAY.INVALID_BOT_ACTIVITY.value.replaceAll(' ', '_'), {
          ns: 'dashboard',
        }),
        THREAT_GROUPS_TO_DISPLAY.INVALID_BOT_ACTIVITY.color
      ),
      [THREAT_GROUPS_TO_DISPLAY.INVALID_MALICIOUS_ACTIVITY.value]: new ThreatGroupData(
        i18n.t(THREAT_GROUPS_TO_DISPLAY.INVALID_MALICIOUS_ACTIVITY.value.replaceAll(' ', '_'), {
          ns: 'dashboard',
        }),
        THREAT_GROUPS_TO_DISPLAY.INVALID_MALICIOUS_ACTIVITY.color
      ),
      [THREAT_GROUPS_TO_DISPLAY.INVALID_SUSPICIOUS_ACTIVITY.value]: new ThreatGroupData(
        i18n.t(THREAT_GROUPS_TO_DISPLAY.INVALID_SUSPICIOUS_ACTIVITY.value.replaceAll(' ', '_'), {
          ns: 'dashboard',
        }),
        THREAT_GROUPS_TO_DISPLAY.INVALID_SUSPICIOUS_ACTIVITY.color
      ),
      [THREAT_GROUPS_TO_DISPLAY.MARKETING_OPTIMIZATION_RULES.value]: new ThreatGroupData(
        i18n.t(THREAT_GROUPS_TO_DISPLAY.MARKETING_OPTIMIZATION_RULES.value.replaceAll(' ', '_'), {
          ns: 'dashboard',
        }),
        THREAT_GROUPS_TO_DISPLAY.MARKETING_OPTIMIZATION_RULES.color
      ),
    },
  }),
  byDomain() {
    return {
      [TREND_CATEGORIES.VISITS]: {},
      ...this.buildThreatGroupsBreakdown(),
    }
  },
  byDate() {
    return {
      [TREND_CATEGORIES.INVALID_RATE]: {},
      ...this.byDomain(),
    }
  },
}

export class DatedAndDomainReducedTrendsAndThreatGroups {
  byDate: ReducedTrendsAndThreatGroups
  byDomain: Record<string, Omit<ReducedTrendsAndThreatGroups, typeof TREND_CATEGORIES.INVALID_RATE>>
  constructor() {
    this.byDate = reducedTrendsAndThreatGroupsBuilder.byDate()
    this.byDomain = {}
  }
}

export type ReducedThreatGroupsBreakdown = {
  totalThreats: number
  data: Array<ThreatGroupData>
  drillDownSeries: Array<ThreatGroupDrillDownPart>
}

export type ReducedResult = Record<keyof typeof TREND_CATEGORIES, Array<[number, number]>> & {
  threatGroupsBreakdown: ReducedThreatGroupsBreakdown
  byDomain: Record<string, ReducedThreatGroupsBreakdown & { visits: Array<[number, number]> }>
}

export type ThreatGroupDrillDownPart = {
  type: 'pie'
  name: string
  id: string
  dataObj: { [key: string]: number }
  data: [string, number][]
}

export class ThreatGroupData {
  name: string
  color: string
  y: number
  drilldown: string
  drilldownData: ThreatGroupDrillDownPart
  constructor(name: string, color: string) {
    this.name = name
    this.y = 0
    this.drilldown = name
    this.drilldownData = {
      type: 'pie',
      name: name,
      id: name,
      data: [],
      dataObj: {},
    } as ThreatGroupDrillDownPart
    this.color = color
  }
}

export type InvalidVisitsTrendsAndThreatGroupsBotzappingData = {
  datedSummary: Array<BotzappingDatedSummary> | undefined
  insights: Array<BottzappingInsight> | undefined
}

export type TableRowTitle = {
  ip: string
  country: string
  lastClick: string
  domain: string
  threatType: string
  count: string
  threatGroup: string
  campaign: string
  platform: string
  application: number
}

export type Filters = {
  startDate: number
  endDate: number
  page: number
  column: string
  order: number
  keyword?: string
  pageType?: string[]
  isHomePage?: boolean
  timezone: string
} & advancedFilters

type advancedFilters = {
  countries?: string[]
  campaigns?: string[]
  threatGroups?: string[]
  threatTypes?: string[]
  domains?: string[]
}

export type RowsResponse = {
  data: {
    ips: TableRowTitle[]
    count: number
  }
}

export const DOMAIN_TAB_NAME = {
  active: 'Active',
  pending: 'Pending',
} as const

type TabName = typeof DOMAIN_TAB_NAME
export type SelectedDomainTabType = TabName[keyof typeof DOMAIN_TAB_NAME]
